import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, Play, ChevronLeft, ChevronRight, Bookmark,
  BookmarkPlus, NotebookPen, FileText, Download, Loader2, HelpCircle, MessageSquare, X, Video, File as FileIcon,
  Lock, ClipboardList,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LoadingBlock } from "@/components/Pagination";
import { LessonVideoPlayer } from "@/components/learner/LessonVideoPlayer";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CoursePlayerPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= 768);
  // User-controlled: whether the sidebar auto-hides on mobile after picking
  // a lesson. Persisted so learners keep their preference across sessions.
  const [autoHideSidebar, setAutoHideSidebar] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("player.autoHideSidebar");
    return stored === null ? true : stored === "true";
  });
  useEffect(() => {
    try { window.localStorage.setItem("player.autoHideSidebar", String(autoHideSidebar)); } catch { /* ignore */ }
  }, [autoHideSidebar]);
  // Auto-advance countdown state — triggered by video onCompleted callback.
  // Learner can cancel or skip during a 5-second window.
  const [autoAdvance, setAutoAdvance] = useState(null);   // {seconds, nextLessonId}
  const autoAdvanceTimerRef = useRef(null);
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("player.autoAdvance");
    return stored === null ? true : stored === "true";
  });
  useEffect(() => {
    try { window.localStorage.setItem("player.autoAdvance", String(autoAdvanceEnabled)); } catch { /* ignore */ }
  }, [autoAdvanceEnabled]);
  const cancelAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current) { clearInterval(autoAdvanceTimerRef.current); autoAdvanceTimerRef.current = null; }
    setAutoAdvance(null);
  }, []);
  useEffect(() => () => cancelAutoAdvance(), [cancelAutoAdvance]);
  // Cancel any pending advance when the learner navigates manually.
  useEffect(() => { cancelAutoAdvance(); }, [currentLesson?.id, cancelAutoAdvance]);
  // Auto-hide effect: when the learner picks a new lesson on mobile with
  // auto-hide enabled, collapse the drawer so the video takes the full width.
  useEffect(() => {
    if (!currentLesson || !autoHideSidebar) return;
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [currentLesson?.id, autoHideSidebar]);
  const [tab, setTab] = useState("player"); // player | notes | resources | doubts
  const [newNote, setNewNote] = useState("");
  const [newDoubt, setNewDoubt] = useState("");
  const [videoBlob, setVideoBlob] = useState(null);
  const videoRef = useRef(null);

  // Phase L1/L2 — course-linked exams
  const [currentExamLink, setCurrentExamLink] = useState(null);
  const [startingExam, setStartingExam] = useState(false);

  // Track whether the initial current-lesson pick has happened. loadCtx()
  // is called after every completion / manual complete to refresh progress
  // counters — WITHOUT this ref it would also snap currentLesson back to
  // the first-uncompleted lesson, which (a) cancels the 5-second auto-
  // advance countdown before it can render, and (b) can pick a wrong
  // lesson if the server hasn't yet flipped progress to "completed" for
  // the row that just finished.
  const initialLessonPickedRef = useRef(false);
  // When auto-advance (either the 5-second countdown or the explicit
  // "Play now" button) moves us to the next lesson, this flag is set so
  // the freshly-mounted video player auto-plays instead of waiting on a
  // manual click. Cleared on manual sidebar / Prev / Next navigation so
  // those don't accidentally autoplay.
  const shouldAutoPlayNextRef = useRef(false);

  const loadCtx = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/learner/my-courses/${assignmentId}/player`);
      setCtx(data.data);
      if (!initialLessonPickedRef.current) {
        // ---- Resume-lesson picker -----------------------------------
        // The old logic picked the FIRST lesson whose progress wasn't
        // "completed" — which meant that if a learner had watched
        // lessons 1-2 partially (or 1 was completed but re-synced as
        // in_progress by a heartbeat), they'd be dropped back on
        // lesson 1 after login. Correct behaviour: resume from the
        // lesson the learner *actually* touched last, using the
        // backend-tracked `progress.last_accessed_at`. If that lesson
        // is already completed, jump to the next unlocked lesson so
        // learners don't get stuck re-watching a finished video.
        const flat = data.data.modules.flatMap((m) => m.lessons || []);
        let resume = null;
        if (flat.length > 0) {
          const touched = flat
            .filter((l) => l.progress?.last_accessed_at)
            .sort((a, b) =>
              new Date(b.progress.last_accessed_at).getTime()
              - new Date(a.progress.last_accessed_at).getTime());
          if (touched.length > 0) {
            const last = touched[0];
            if (last.progress?.status === "completed") {
              const idx = flat.findIndex((l) => l.id === last.id);
              // Prefer the next unlocked lesson after the last one
              // completed. If none exists (course finished / everything
              // locked), stay on the last completed lesson.
              resume = flat.slice(idx + 1).find((l) => !l.is_locked) || last;
            } else {
              resume = last;
            }
          }
          if (!resume) {
            resume = flat.find((l) => l.progress?.status !== "completed") || flat[0];
          }
          setCurrentLesson(resume);
        }
        initialLessonPickedRef.current = true;
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [assignmentId]);
  useEffect(() => { loadCtx(); }, [loadCtx]);

  const loadLesson = useCallback(async (lessonId) => {
    if (!lessonId) return;
    try {
      const { data } = await api.get(`/learner/lessons/${lessonId}`);
      setLessonData(data.data);
      setNewNote(""); setNewDoubt("");
      setVideoBlob(null);
      // Auto-start
      try { await api.post(`/learner/lessons/${lessonId}/start`); } catch { /* silent */ }
      // Load protected video/doc via blob (Authorization cannot go via native src)
      if (data.data.lesson.content_file_path) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/files/${data.data.lesson.content_file_path}`, { credentials: "include" });
          if (res.ok) {
            const blob = await res.blob();
            setVideoBlob(URL.createObjectURL(blob));
          }
        } catch { /* silent */ }
      }
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { if (currentLesson) loadLesson(currentLesson.id); }, [currentLesson, loadLesson]);

  // NOTE: Video progress + completion is owned entirely by
  // <LessonVideoPlayer /> which posts to /video-progress with the
  // secure `unique_watched_seconds` metric and anti-skip guards. The
  // old raw-<video> onTime/onEnded handler that fired /progress with
  // completion_percentage was REMOVED because it let a learner seek
  // to 80 % and instantly close the lesson (production bug).

  const markComplete = async () => {
    try {
      await api.post(`/learner/lessons/${currentLesson.id}/complete`);
      toast.success("Lesson marked complete.");
      loadCtx();
      loadLesson(currentLesson.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleBookmark = async () => {
    try {
      if (lessonData?.bookmark) {
        await api.delete(`/learner/bookmarks/${lessonData.bookmark.id}`);
        toast.success("Bookmark removed.");
      } else {
        await api.post(`/learner/lessons/${currentLesson.id}/bookmark`, { lesson_id: currentLesson.id });
        toast.success("Bookmarked.");
      }
      loadLesson(currentLesson.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const ts = videoRef.current ? Math.round(videoRef.current.currentTime) : null;
      await api.post(`/learner/lessons/${currentLesson.id}/notes`, { note_text: newNote, video_timestamp_seconds: ts });
      setNewNote("");
      loadLesson(currentLesson.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delNote = async (id) => {
    try { await api.delete(`/learner/notes/${id}`); loadLesson(currentLesson.id); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submitDoubt = async () => {
    if (!newDoubt.trim()) return;
    try {
      await api.post(`/learner/lessons/${currentLesson.id}/doubts`, { lesson_id: currentLesson.id, doubt_text: newDoubt });
      setNewDoubt(""); toast.success("Doubt submitted.");
      loadLesson(currentLesson.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const flatLessons = ctx?.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, module: m }))) || [];
  const currentIndex = flatLessons.findIndex((l) => l.id === currentLesson?.id);
  const prev = currentIndex > 0 ? flatLessons[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < flatLessons.length - 1 ? flatLessons[currentIndex + 1] : null;

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center"><LoadingBlock label="Loading course…" /></div>;
  }
  if (!ctx) return null;

  const cp = ctx.course_progress;
  const percent = cp?.progress_percentage || 0;
  const watermark = `${ctx.learner.full_name} · ${ctx.learner.email} · ${ctx.learner.mobile || ""}`;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col" data-testid="course-player">
      {/* Top bar */}
      <header className="border-b border-slate-800 bg-slate-900 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => setSidebarOpen((v) => !v)} data-testid="player-sidebar-toggle" className="rounded-md p-1.5 hover:bg-slate-800 md:hidden">          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <button onClick={() => navigate("/app/learner/courses")} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="player-exit">
          <ArrowLeft className="h-3.5 w-3.5" /> Exit
        </button>
        <div className="mx-4 h-4 w-px bg-slate-700 hidden sm:block" />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Now learning</div>
          <div className="text-sm font-bold text-white truncate">{ctx.course.course_title}</div>
        </div>
        <div className="hidden sm:block text-right">
          <div className="text-xs text-slate-400">{ctx.learner.full_name}</div>
          <div className="text-[10px] text-slate-500">{percent}% complete · {cp?.completed_lessons || 0}/{cp?.required_lessons || 0} lessons</div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-slate-800 flex-shrink-0">
        <div className="h-full bg-[#329112] transition-all" style={{ width: `${percent}%` }} />
      </div>

      <div className="flex flex-1 min-h-0 relative">
        {/* Tap-outside backdrop — mobile only. Clicking anywhere outside the
            drawer closes it (manual-hide gesture). */}
        {sidebarOpen && (
          <div
            className="absolute inset-0 z-10 bg-black/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="player-sidebar-backdrop"
            aria-hidden
          />
        )}
        {/* Sidebar — full-width overlay on mobile, docked column on desktop */}
        {sidebarOpen && (
          <aside
            className="absolute inset-y-0 left-0 z-20 w-72 max-w-[85vw] flex-shrink-0 border-r border-slate-800 bg-slate-900 overflow-y-auto shadow-xl md:static md:z-0 md:w-80 md:max-w-none md:shadow-none"
            data-testid="player-sidebar"
          >
            {/* Mobile-only header: close button + auto-hide toggle. Hidden on md+. */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-slate-800 bg-slate-900/95 px-3 py-2 backdrop-blur md:hidden">
              <div className="flex items-center gap-3">
                <label
                  className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-300"
                  data-testid="player-sidebar-autohide-wrap"
                >
                  <input
                    type="checkbox"
                    checked={autoHideSidebar}
                    onChange={(e) => setAutoHideSidebar(e.target.checked)}
                    data-testid="player-sidebar-autohide-toggle"
                    className="h-3 w-3 accent-[#329112]"
                  />
                  Auto-hide
                </label>
                <label
                  className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-300"
                  data-testid="player-autoadvance-wrap"
                >
                  <input
                    type="checkbox"
                    checked={autoAdvanceEnabled}
                    onChange={(e) => setAutoAdvanceEnabled(e.target.checked)}
                    data-testid="player-autoadvance-toggle"
                    className="h-3 w-3 accent-[#329112]"
                  />
                  Auto-play next
                </label>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                data-testid="player-sidebar-close"
                aria-label="Close lessons menu"
                className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-[10px] font-bold text-slate-200 hover:bg-slate-700"
              >
                <X className="h-3 w-3" /> Close
              </button>
            </div>
            {ctx.modules.map((m) => (
              <div key={m.id} className="border-b border-slate-800">
                <div className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{m.display_order}. {m.module_title}</div>
                {(m.lessons || []).length === 0 ? (
                  <div className="px-4 pb-3 text-[11px] text-slate-500 italic">No lessons published yet.</div>
                ) : m.lessons.map((l) => {
                  const locked = !!l.is_locked;
                  const progressPct = Number(l.progress?.completion_percentage || 0);
                  const status = locked
                    ? "locked"
                    : l.progress?.status === "completed"
                      ? "completed"
                      : progressPct > 0 ? "in_progress" : "not_started";
                  return (
                    <button
                      key={l.id}
                      onClick={() => {
                        if (locked) {
                          toast.error(l.lock_reason || "Complete the previous video 100% to unlock this lesson.");
                          return;
                        }
                        setCurrentLesson(l);
                      }}
                      disabled={locked}
                      aria-disabled={locked}
                      className={`w-full px-4 py-2.5 flex items-start gap-3 text-left hover:bg-slate-800 ${currentLesson?.id === l.id ? "bg-slate-800 border-l-2 border-[#329112]" : ""} ${locked ? "opacity-60 cursor-not-allowed hover:bg-transparent" : ""}`}
                      data-testid={`player-lesson-${l.id}`}
                      title={locked ? (l.lock_reason || "Locked") : ""}
                    >
                      <span className="mt-0.5 flex-shrink-0">
                        {locked
                          ? <Lock className="h-4 w-4 text-amber-400" />
                          : status === "completed"
                            ? <CheckCircle2 className="h-4 w-4 text-[#329112]" />
                            : <Circle className="h-4 w-4 text-slate-500" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-200 truncate">{l.lesson_title}</span>
                        <span className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
                          {l.lesson_type === "video" ? <Video className="h-3 w-3" /> : <FileIcon className="h-3 w-3" />}
                          <span className="uppercase">{l.lesson_type}</span>
                          <span className="text-slate-600">·</span>
                          <span>{l.estimated_duration_minutes}m</span>
                          {!locked && status === "in_progress" && (
                            <>
                              <span className="text-slate-600">·</span>
                              <span className="text-amber-300">{progressPct}%</span>
                            </>
                          )}
                        </span>
                        {locked && (
                          <span className="mt-1 block text-[10px] text-amber-300/80" data-testid={`player-lesson-lock-hint-${l.id}`}>
                            🔒 Complete the previous video 100% to unlock.
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Course-linked exams section (Phase L1) */}
            {(ctx.linked_exams || []).length > 0 && (
              <div className="border-b border-slate-800">
                <div className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2">
                  <ClipboardList className="h-3.5 w-3.5" /> Final Assessment
                </div>
                {ctx.linked_exams.map((lnk) => {
                  const locked = !lnk.unlock_state?.is_unlocked;
                  return (
                    <button
                      key={lnk.id}
                      onClick={() => { setCurrentExamLink(lnk); setCurrentLesson(null); }}
                      className={`w-full px-4 py-2.5 flex items-start gap-3 text-left hover:bg-slate-800 ${currentExamLink?.id === lnk.id ? "bg-slate-800 border-l-2 border-amber-400" : ""}`}
                      data-testid={`player-exam-link-${lnk.id}`}
                    >
                      <span className="mt-0.5 flex-shrink-0">
                        {locked ? <Lock className="h-4 w-4 text-slate-500" /> : <ClipboardList className="h-4 w-4 text-amber-400" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-200 truncate">{lnk.display_title || lnk.exam?.exam_title}</span>
                        <span className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
                          <span className="uppercase">{(lnk.exam_type || "").replace(/_/g, " ")}</span>
                          <span className="text-slate-600">·</span>
                          <span>{lnk.exam?.duration_minutes}m · {lnk.exam?.total_questions} Q</span>
                        </span>
                        {locked && (
                          <span className="mt-1 text-[10px] text-amber-300/80 truncate">🔒 Locked</span>
                        )}
                        {!locked && lnk.attempts_summary?.submitted_count > 0 && (
                          <span className="mt-1 text-[10px] text-emerald-300/80 truncate">
                            Last score: {lnk.attempts_summary.latest_score}% · {lnk.attempts_summary.latest_status}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </aside>
        )}

        {/* Main */}
        <main className="flex-1 overflow-y-auto">
          {currentExamLink ? (
            <ExamLinkPane
              link={currentExamLink}
              onBack={() => setCurrentExamLink(null)}
              onStart={async () => {
                setStartingExam(true);
                try {
                  const { data } = await api.post(`/learner/course-exam-links/${currentExamLink.id}/start-exam`);
                  navigate(`/app/learner/exams/${data.data.exam_assignment_id}`);
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally { setStartingExam(false); }
              }}
              starting={startingExam}
            />
          ) : !currentLesson || !lessonData ? (
            <div className="flex h-full items-center justify-center text-slate-500">Select a lesson from the sidebar.</div>
          ) : (
            <div className="p-6 space-y-4 max-w-5xl mx-auto">
              {/* Lesson content area */}
              <div className="relative rounded-xl overflow-hidden bg-black min-h-[300px]" data-testid="player-content">
                {lessonData.lesson.lesson_type === "video" ? (
                  <LessonVideoPlayer
                    lessonId={lessonData.lesson.id}
                    autoPlay={shouldAutoPlayNextRef.current}
                    onPlayStarted={() => { shouldAutoPlayNextRef.current = false; }}
                    onCompleted={() => {
                      loadCtx();
                      loadLesson(lessonData.lesson.id);
                      // Kick off the 5-second auto-advance if enabled and a
                      // next lesson exists. Cancellable via UI or by
                      // manually navigating.
                      if (!autoAdvanceEnabled || !next?.id) return;
                      const nextId = next.id;
                      setAutoAdvance({ seconds: 5, nextLessonId: nextId, nextTitle: next.lesson_title });
                      autoAdvanceTimerRef.current = setInterval(() => {
                        setAutoAdvance((s) => {
                          if (!s) return null;
                          if (s.seconds <= 1) {
                            clearInterval(autoAdvanceTimerRef.current);
                            autoAdvanceTimerRef.current = null;
                            const target = flatLessons.find((l) => l.id === nextId);
                            if (target) {
                              shouldAutoPlayNextRef.current = true;
                              setCurrentLesson(target);
                            }
                            return null;
                          }
                          return { ...s, seconds: s.seconds - 1 };
                        });
                      }, 1000);
                    }}
                  />
                ) : ["pdf", "ppt", "pptx", "doc", "docx"].includes(lessonData.lesson.lesson_type) && lessonData.lesson.content_file_path ? (
                  <DocumentPane
                    src={`${BACKEND_URL}/api/files/${lessonData.lesson.content_file_path}`}
                    title={lessonData.lesson.lesson_title}
                  />
                ) : lessonData.lesson.lesson_type === "quiz" ? (
                  <QuizLessonPane
                    lesson={lessonData.lesson}
                    linkedExam={lessonData.linked_exam}
                    onStarted={(assignmentId) => navigate(`/app/learner/exams/${assignmentId}`)}
                  />
                ) : lessonData.lesson.lesson_type === "text" && lessonData.lesson.content_text ? (
                  <div className="bg-white text-slate-900 p-8 min-h-[400px] whitespace-pre-line" data-testid="player-text">
                    {lessonData.lesson.content_text}
                  </div>
                ) : (
                  <div className="flex h-[300px] items-center justify-center text-slate-500 text-sm">
                    <div className="text-center space-y-2">
                      <FileText className="h-8 w-8 mx-auto text-slate-600" />
                      <div>Content will appear once your instructor uploads it.</div>
                    </div>
                  </div>
                )}

                {/* Auto-advance countdown — CENTERED overlay on top of the
                    video so the "Playing next in Ns" prompt is unmissable.
                    Learner can cancel or jump to the next lesson instantly.
                    Backdrop is click-through except on the card itself. */}
                {autoAdvance && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
                       data-testid="auto-advance-overlay">
                    <div className="pointer-events-auto w-[min(92%,26rem)] rounded-2xl border border-emerald-400/50 bg-slate-950/90 backdrop-blur-md px-5 py-5 text-center shadow-2xl shadow-emerald-500/20 animate-in fade-in zoom-in-95 duration-200"
                         data-testid="auto-advance-banner">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-emerald-400/60 bg-emerald-500/10">
                        <span className="text-2xl font-black text-emerald-300 tabular-nums" data-testid="auto-advance-seconds">
                          {autoAdvance.seconds}
                        </span>
                      </div>
                      <div className="mt-3 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300/80">
                        Playing next in {autoAdvance.seconds}s
                      </div>
                      <div className="mt-1 line-clamp-2 text-sm font-semibold text-white">
                        {autoAdvance.nextTitle}
                      </div>
                      <div className="mt-4 flex items-center justify-center gap-2">
                        <button type="button" onClick={cancelAutoAdvance}
                                data-testid="auto-advance-cancel"
                                className="rounded-full border border-slate-600 bg-transparent px-4 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-800">
                          Cancel
                        </button>
                        <button type="button"
                                onClick={() => { const t = flatLessons.find((l) => l.id === autoAdvance.nextLessonId); if (t) { shouldAutoPlayNextRef.current = true; setCurrentLesson(t); } cancelAutoAdvance(); }}
                                data-testid="auto-advance-play-now"
                                className="rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-400">
                          Play now
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lesson meta + actions */}
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{currentLesson.module?.module_title}</div>
                    <h2 className="text-xl font-bold text-white">{lessonData.lesson.lesson_title}</h2>
                    {lessonData.lesson.lesson_description && <p className="mt-1 text-sm text-slate-400">{lessonData.lesson.lesson_description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={toggleBookmark} className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700" data-testid="player-bookmark">
                      {lessonData.bookmark ? <><Bookmark className="inline h-3.5 w-3.5 mr-1 fill-amber-400 text-amber-400" /> Bookmarked</> : <><BookmarkPlus className="inline h-3.5 w-3.5 mr-1" /> Bookmark</>}
                    </button>
                    {lessonData.progress?.status === "completed" ? (
                      <div className="rounded-md bg-emerald-900/40 border border-emerald-700 px-3 py-1.5 text-xs font-bold text-emerald-300">
                        <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" /> Completed
                      </div>
                    ) : lessonData.lesson.lesson_type === "video" ? (
                      <div className="rounded-md border border-amber-500/40 bg-amber-900/30 px-3 py-1.5 text-[11px] font-semibold text-amber-200"
                           title="Video lessons auto-complete when the required watch percentage is met."
                           data-testid="player-video-autocomplete-hint">
                        Auto-completes at required watch time
                      </div>
                    ) : (
                      <button onClick={markComplete} className="rounded-md bg-[#329112] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#256a0d]" data-testid="player-complete">
                        <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" /> Mark complete
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-slate-800 pt-3">
                  <button onClick={() => prev && setCurrentLesson(prev)} disabled={!prev} className="text-xs text-slate-400 hover:text-white disabled:opacity-30 flex items-center gap-1" data-testid="player-prev">
                    <ChevronLeft className="h-4 w-4" /> {prev?.lesson_title || "Prev"}
                  </button>
                  {(() => {
                    // "Next lesson" gate: block navigation until the current
                    // (video) lesson has been completed 100 %. For non-video
                    // lessons the existing Complete flow already gates progress.
                    const currentIsVideo = lessonData?.lesson?.lesson_type === "video";
                    const currentDone = lessonData?.progress?.status === "completed";
                    const nextLocked = !next || !!next.is_locked || (currentIsVideo && !currentDone);
                    const hint = !next
                      ? ""
                      : next.is_locked
                        ? (next.lock_reason || "Complete the previous video 100% to unlock.")
                        : (currentIsVideo && !currentDone)
                          ? "Please complete this video 100% to unlock the next video."
                          : "";
                    return (
                      <button
                        onClick={() => {
                          if (nextLocked) {
                            if (hint) toast.error(hint);
                            return;
                          }
                          setCurrentLesson(next);
                        }}
                        disabled={nextLocked}
                        title={hint}
                        className={`text-xs flex items-center gap-1 ${nextLocked ? "text-slate-500 cursor-not-allowed" : "text-slate-400 hover:text-white"}`}
                        data-testid="player-next"
                      >
                        {nextLocked && next && <Lock className="h-3 w-3 text-amber-400" />}
                        {next?.lesson_title || "Next"} <ChevronRight className="h-4 w-4" />
                      </button>
                    );
                  })()}
                </div>

                {/* Prominent Play-Next CTA — appears when the current lesson
                    is completed AND a next (unlocked) lesson exists. Solves
                    the "video finished but I can't find how to advance"
                    dead-end. Independent of the auto-advance countdown so
                    learners who disabled auto-advance still get a clear
                    manual jump. */}
                {(() => {
                  const currentDone = lessonData?.progress?.status === "completed";
                  const canAdvance = currentDone && next && !next.is_locked;
                  if (!canAdvance) return null;
                  return (
                    <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-900/20 p-3 flex items-center justify-between gap-3"
                         data-testid="player-play-next-cta">
                      <div className="text-xs text-emerald-200">
                        <div className="font-bold uppercase tracking-widest">Lesson complete</div>
                        <div className="text-slate-300 line-clamp-1">Ready for: <b>{next.lesson_title}</b></div>
                      </div>
                      <button
                        onClick={() => { shouldAutoPlayNextRef.current = true; setCurrentLesson(next); }}
                        className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 flex items-center gap-1"
                        data-testid="btn-play-next-video">
                        <Play className="h-3.5 w-3.5" /> Play next video
                      </button>
                    </div>
                  );
                })()}
              </div>

              {/* Tabs */}
              <div className="rounded-xl border border-slate-800 bg-slate-900">
                <div className="flex border-b border-slate-800 overflow-x-auto">
                  {[["notes", NotebookPen, "Notes", lessonData.notes.length],
                    ["resources", Download, "Resources", lessonData.resources.length],
                    ["doubts", HelpCircle, "Doubts", lessonData.doubts.length]].map(([id, Icon, label, count]) => (
                    <button key={id} onClick={() => setTab(id)}
                      className={`flex-shrink-0 px-4 py-3 text-xs font-bold uppercase tracking-widest flex items-center gap-2 ${tab === id ? "text-white border-b-2 border-[#329112]" : "text-slate-500 hover:text-slate-300"}`}
                      data-testid={`player-tab-${id}`}>
                      <Icon className="h-3.5 w-3.5" /> {label} {count > 0 && <span className="rounded bg-slate-800 px-1.5 text-[9px]">{count}</span>}
                    </button>
                  ))}
                </div>

                {tab === "notes" && (
                  <div className="p-4 space-y-3" data-testid="player-notes-tab">
                    <div className="flex items-start gap-2">
                      <Textarea rows={2} className="bg-slate-950 border-slate-800 text-slate-200" placeholder="Add a personal note…" value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="player-note-input" />
                      <button onClick={addNote} className="rounded-md bg-[#101876] px-3 py-2 text-xs font-bold text-white" data-testid="player-note-add">Add</button>
                    </div>
                    <div className="space-y-2">
                      {lessonData.notes.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No notes yet.</div>}
                      {lessonData.notes.map((n) => (
                        <div key={n.id} className="rounded-md border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {n.video_timestamp_seconds !== null && n.video_timestamp_seconds !== undefined && (
                              <span className="mr-2 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-mono text-amber-300">
                                {Math.floor(n.video_timestamp_seconds / 60)}:{String(n.video_timestamp_seconds % 60).padStart(2, "0")}
                              </span>
                            )}
                            <span className="whitespace-pre-line">{n.note_text}</span>
                          </div>
                          <button onClick={() => delNote(n.id)} className="text-slate-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "resources" && (
                  <div className="p-4 space-y-2" data-testid="player-resources-tab">
                    {lessonData.resources.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No downloadable resources for this lesson.</div>}
                    {lessonData.resources.map((r) => (
                      <a key={r.id} href={`${BACKEND_URL}/api/files/${r.file_path}`} target="_blank" rel="noreferrer"
                         className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950/50 p-3 hover:border-[#329112]">
                        <div className="flex items-center gap-2">
                          <Download className="h-4 w-4 text-slate-400" />
                          <span className="text-sm font-semibold text-slate-200">{r.resource_title}</span>
                          <span className="text-[10px] uppercase tracking-wider bg-slate-800 px-1.5 py-0.5 rounded text-slate-500">{r.resource_type}</span>
                        </div>
                        {r.download_allowed && <span className="text-[10px] text-emerald-400">Downloadable</span>}
                      </a>
                    ))}
                  </div>
                )}

                {tab === "doubts" && (
                  <div className="p-4 space-y-3" data-testid="player-doubts-tab">
                    <div className="flex items-start gap-2">
                      <Textarea rows={2} className="bg-slate-950 border-slate-800 text-slate-200" placeholder="Ask a doubt about this lesson…" value={newDoubt} onChange={(e) => setNewDoubt(e.target.value)} data-testid="player-doubt-input" />
                      <button onClick={submitDoubt} className="rounded-md bg-[#101876] px-3 py-2 text-xs font-bold text-white" data-testid="player-doubt-submit">Ask</button>
                    </div>
                    <div className="space-y-2">
                      {lessonData.doubts.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No doubts yet. Ask your instructor anything.</div>}
                      {lessonData.doubts.map((d) => (
                        <div key={d.id} className="rounded-md border border-slate-800 bg-slate-950/50 p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs">
                              <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                              <span className={`rounded px-1.5 py-0.5 font-bold uppercase text-[9px] tracking-widest ${d.status === "answered" ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/40 text-amber-300"}`}>{d.status}</span>
                            </div>
                            <div className="text-[10px] text-slate-500">{d.created_at?.slice(0, 16).replace("T", " ")}</div>
                          </div>
                          <div className="text-sm text-slate-200 whitespace-pre-line">{d.doubt_text}</div>
                          {d.faculty_reply && (
                            <div className="mt-2 rounded border-l-2 border-[#329112] pl-3 py-1">
                              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Instructor reply</div>
                              <div className="text-sm text-slate-200 whitespace-pre-line">{d.faculty_reply}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}


function ExamLinkPane({ link, onBack, onStart, starting }) {
  const s = link.unlock_state || {};
  const summary = link.attempts_summary || {};
  const canReattempt = summary.attempt_count < (link.exam?.maximum_attempts || 1);
  const downloadPdf = async (attemptId) => {
    try {
      const { data } = await api.get(`/learner/exam-attempts/${attemptId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `post_test_${link.exam?.exam_code}_attempt.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="player-exam-pane">
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to lessons
      </button>

      <div className="rounded-xl bg-slate-900 border border-slate-800 p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 text-[10px] font-bold uppercase px-2 py-0.5">{(link.exam_type || "").replace(/_/g, " ")}</span>
          {link.is_mandatory && <span className="rounded bg-rose-500/10 text-rose-300 border border-rose-500/30 text-[10px] font-bold uppercase px-2 py-0.5">Mandatory</span>}
          {link.required_for_certificate && <span className="rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold uppercase px-2 py-0.5">Required for certificate</span>}
        </div>
        <h1 className="mt-3 text-2xl font-black text-white">{link.display_title || link.exam?.exam_title}</h1>
        <div className="mt-1 text-xs text-slate-400">Exam code: <b className="text-slate-200">{link.exam?.exam_code}</b></div>

        {!s.is_unlocked ? (
          <div className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/5 p-5 flex items-start gap-3" data-testid="exam-pane-locked">
            <Lock className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-amber-200">Locked</div>
              <div className="mt-1 text-sm text-amber-100">{s.message}</div>
              {typeof s.required_lessons_total === "number" && s.required_lessons_total > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] text-amber-200/70 mb-1">Progress: {s.required_lessons_completed} / {s.required_lessons_total} required lessons</div>
                  <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 transition-all" style={{ width: `${Math.min(100, (s.required_lessons_completed / Math.max(1, s.required_lessons_total)) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="exam-pane-unlocked">
            <div className="text-sm font-bold text-emerald-200">Your post-test is now available.</div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Total questions" value={link.exam?.total_questions} />
              <Stat label="Time limit" value={`${link.exam?.duration_minutes} min`} />
              <Stat label="Passing %" value={`${link.exam?.passing_percentage}%`} />
              <Stat label="Attempts" value={`${summary.attempt_count || 0} / ${link.exam?.maximum_attempts}`} />
            </div>
            <div className="mt-5">
              {canReattempt ? (
                <button onClick={onStart} disabled={starting}
                  className="rounded-lg bg-[#329112] hover:bg-[#256a0d] text-white font-bold px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="exam-pane-start"
                >
                  {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {summary.attempt_count > 0 ? "Reattempt post-test" : "Start post-test"}
                </button>
              ) : (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-3">
                  You have reached the maximum number of attempts.
                </div>
              )}
            </div>
          </div>
        )}

        {summary.attempt_count > 0 && (
          <div className="mt-6">
            <div className="text-sm font-bold text-slate-200 mb-2">Attempt history</div>
            <div className="rounded-lg border border-slate-800 divide-y divide-slate-800">
              {summary.attempts.map((a) => (
                <div key={a.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`exam-attempt-${a.id}`}>
                  <span className="text-xs font-bold text-slate-400 font-mono">#{a.attempt_number}</span>
                  <span className="text-xs text-slate-300">{a.submitted_at?.slice(0, 16).replace("T", " ") || "in progress"}</span>
                  {a.status !== "in_progress" && (
                    <>
                      <span className="text-xs font-bold text-slate-200">{a.marks_obtained}/{a.total_marks} · {a.percentage}%</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${a.pass_fail_status === "passed" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                        {a.pass_fail_status || a.status}
                      </span>
                      {(link.exam?.allow_post_test_pdf_download !== false) && (
                        <button onClick={() => downloadPdf(a.id)} className="ml-auto rounded-md border border-slate-700 bg-slate-800 text-xs font-bold text-slate-200 px-3 py-1.5 hover:bg-slate-700 flex items-center gap-1" data-testid={`exam-pdf-${a.id}`}>
                          <Download className="h-3 w-3" /> Post-Test PDF
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-slate-800/50 p-3 border border-slate-700/60">
      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">{label}</div>
      <div className="text-lg font-black text-white">{value}</div>
    </div>
  );
}


function QuizLessonPane({ lesson, linkedExam, onStarted }) {
  const [starting, setStarting] = useState(false);
  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/learner/lessons/${lesson.id}/start-quiz`);
      onStarted?.(data.data.exam_assignment_id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setStarting(false); }
  };
  if (!linkedExam) {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-2 bg-slate-950 text-center text-sm text-slate-400" data-testid="player-quiz-empty">
        <HelpCircle className="h-8 w-8 text-slate-600" />
        <div>This quiz has not been set up yet.</div>
        <div className="text-xs text-slate-500">Your instructor will attach the quiz shortly.</div>
      </div>
    );
  }
  return (
    <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 bg-slate-950 p-8 text-center text-white" data-testid="player-quiz">
      <HelpCircle className="h-10 w-10 text-amber-400" />
      <div className="text-xs font-bold uppercase tracking-widest text-amber-300">Quiz lesson</div>
      <div className="text-lg font-black">{linkedExam.exam_title}</div>
      {linkedExam.exam_description && (
        <p className="max-w-lg text-xs text-slate-400">{linkedExam.exam_description}</p>
      )}
      <div className="grid grid-cols-3 gap-3 pt-2">
        <Stat label="Questions" value={linkedExam.total_questions ?? 0} />
        <Stat label="Duration" value={`${linkedExam.duration_minutes ?? 0}m`} />
        <Stat label="Pass mark" value={`${linkedExam.passing_percentage ?? 0}%`} />
      </div>
      <button
        onClick={start}
        disabled={starting}
        data-testid="player-quiz-start"
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-60"
      >
        {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        Start quiz
      </button>
    </div>
  );
}


function DocumentPane({ src, title }) {
  const [state, setState] = useState({ status: "loading", size: 0 });
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", size: 0 });
    // HEAD probe: verify the file exists AND isn't a suspiciously small stub
    // (a genuine PDF is >= 1KB — anything smaller almost always means the
    // upload was corrupted or an empty placeholder file was left in storage).
    (async () => {
      try {
        const r = await fetch(src, { method: "HEAD", credentials: "include" });
        if (cancelled) return;
        if (!r.ok) { setState({ status: "error", size: 0, code: r.status }); return; }
        const size = Number(r.headers.get("content-length") || 0);
        if (size > 0 && size < 500) { setState({ status: "corrupt", size }); return; }
        setState({ status: "ok", size });
      } catch (e) {
        if (!cancelled) setState({ status: "error", size: 0, msg: e?.message });
      }
    })();
    return () => { cancelled = true; };
  }, [src]);

  if (state.status === "loading") {
    return (
      <div className="flex h-[300px] items-center justify-center gap-2 bg-slate-50 text-sm text-slate-500" data-testid="player-doc-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading document…
      </div>
    );
  }
  if (state.status === "corrupt") {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-2 bg-amber-50 p-6 text-center text-sm text-amber-900" data-testid="player-doc-corrupt">
        <FileText className="h-8 w-8 text-amber-500" />
        <div className="font-bold">This document appears to be corrupted or empty.</div>
        <div className="text-xs text-amber-700">
          File size is only {state.size} bytes. Please ask your instructor to re-upload the document.
        </div>
        <a href={src} target="_blank" rel="noreferrer" className="mt-1 text-xs underline">
          Try opening in a new tab
        </a>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-2 bg-rose-50 p-6 text-center text-sm text-rose-900" data-testid="player-doc-error">
        <FileText className="h-8 w-8 text-rose-500" />
        <div className="font-bold">Couldn&apos;t load this document.</div>
        <div className="text-xs text-rose-700">
          {state.code === 404 ? "The file is missing from storage." : (state.msg || "Please refresh the page or contact your instructor.")}
        </div>
      </div>
    );
  }
  return (
    <iframe title={title} src={src} className="w-full h-[70vh] bg-white" data-testid="player-doc" />
  );
}
